import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import ServiceMeta from '@/components/ServiceMeta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Music, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const PromotionServicePage = ({ 
  platform, 
  platformName, 
  iconColor, 
  description,
  metaTitle,
  metaDescription,
  url
}) => {
  const { user, isAdminAdil } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    platform_link: '',
    promotion_goal: '',
    quantity: '',
    phone_number: ''
  });
  const [operator, setOperator] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);

  // Options de promotion selon la plateforme
  const promotionOptions = {
    spotify: [
      { value: 'streams', label: 'Vues / Streams - 35$' },
      { value: 'hits', label: 'Hits - 35$' },
      { value: 'followers', label: 'Abonnés / Followers - 35$' },
      { value: 'monthly_listeners', label: 'Auditeurs mensuels - 35$' },
      { value: 'playlists', label: 'Playlists - 35$' },
      { value: 'saves', label: 'Sauvegardes - 35$' }
    ],
    apple_music: [
      { value: 'streams', label: 'Vues / Streams - 35$' },
      { value: 'hits', label: 'Hits - 35$' },
      { value: 'followers', label: 'Abonnés / Followers - 35$' },
      { value: 'downloads', label: 'Téléchargements - 35$' },
      { value: 'playlists', label: 'Playlists - 35$' }
    ],
    boomplay: [
      { value: 'streams', label: 'Vues / Streams - 35$' },
      { value: 'hits', label: 'Hits - 35$' },
      { value: 'followers', label: 'Abonnés / Followers - 35$' },
      { value: 'downloads', label: 'Téléchargements - 35$' },
      { value: 'playlists', label: 'Playlists - 35$' }
    ],
    tidal: [
      { value: 'streams', label: 'Vues / Streams - 35$' },
      { value: 'hits', label: 'Hits - 35$' },
      { value: 'followers', label: 'Abonnés / Followers - 35$' },
      { value: 'playlists', label: 'Playlists - 35$' }
    ]
  };

  const currentOptions = promotionOptions[platform] || promotionOptions.spotify;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.platform_link || !formData.promotion_goal || !formData.quantity) {
      toast.error('Tous les champs requis doivent être remplis');
      return;
    }
    
    // Si non-admin, vérifier téléphone et opérateur
    if (!isAdminAdil && (!formData.phone_number || !operator)) {
      toast.error('Téléphone et opérateur sont requis');
      return;
    }

    setSubmitting(true);
    
    try {
      // Créer la commande de promotion
      const response = await api.post('/promotion/initiate', {
        platform,
        platform_link: formData.platform_link,
        promotion_goal: formData.promotion_goal,
        quantity: formData.quantity,
        phone_number: formData.phone_number || '',
        operator: operator || '',
        amount: isAdminAdil ? 0 : 35,
        is_admin_free: isAdminAdil
      });

      if (response.data.success) {
        setPaymentInitiated(true);
        if (isAdminAdil) {
          toast.success('✅ Commande GRATUITE enregistrée !');
        } else {
          toast.success('✅ Paiement initié ! Vérifiez votre téléphone pour confirmer');
        }
        
        // Attendre quelques secondes puis marquer comme soumis
        setTimeout(() => {
          setSubmitted(true);
        }, 3000);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pb-12">
        <ServiceMeta 
          title={metaTitle}
          description={metaDescription}
          url={url}
        />
        <Header />
        
        <main className="pt-24 max-w-3xl mx-auto px-4">
          <Card className="glass border-zinc-800 text-center">
            <CardContent className="py-12">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-white mb-3">
                Paiement En Cours ! 🎉
              </h2>
              <p className="text-zinc-400 mb-6">
                Votre demande de promotion {platformName} a été enregistrée.
              </p>
              <div className="bg-zinc-900 rounded-lg p-4 mb-6">
                <p className="text-sm text-zinc-400">
                  <strong className="text-white">Prochaines étapes :</strong>
                </p>
                <ul className="text-left text-sm text-zinc-400 mt-2 space-y-1">
                  <li>✓ Confirmez le paiement sur votre téléphone (35$)</li>
                  <li>⏳ Traitement par l'équipe AGRP BOX</li>
                  <li>📧 Vous serez contacté sous 24-48h</li>
                  <li>🚀 Promotion activée sur {platformName}</li>
                </ul>
              </div>
              <Button onClick={() => navigate('/dashboard')} className="bg-[#E50914]">
                Retour au Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <ServiceMeta 
        title={metaTitle}
        description={metaDescription}
        url={url}
      />
      <Header />
      
      <main className="pt-24 max-w-3xl mx-auto px-4">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour
        </Button>

        <Card className="glass border-zinc-800">
          <CardHeader>
            <div className={`w-20 h-20 ${iconColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <Music className="text-white" size={40} />
            </div>
            <CardTitle className="text-white text-center text-2xl">
              Promouvoir votre compte {platformName}
            </CardTitle>
            <p className="text-zinc-400 text-center">{description}</p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className={`${isAdminAdil ? 'bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-800' : 'bg-gradient-to-r from-orange-900/20 to-red-900/20 border border-orange-800'} rounded-lg p-4 mb-6`}>
                <p className={`${isAdminAdil ? 'text-green-200' : 'text-orange-200'} text-center font-bold text-xl`}>
                  Prix: {isAdminAdil ? 'GRATUIT (Admin Adil)' : '35$'}
                </p>
              </div>

              {/* ÉTAPE 1 : Lien de la plateforme */}
              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
                <h3 className="text-white font-bold mb-3">📎 ÉTAPE 1 : Lien de la plateforme</h3>
                <Label className="text-white">
                  {platform === 'spotify' && 'Collez votre lien Spotify ici *'}
                  {platform === 'apple_music' && 'Collez votre lien Apple Music ici *'}
                  {platform === 'boomplay' && 'Collez votre lien Boomplay ici *'}
                  {platform === 'tidal' && 'Collez votre lien Tidal ici *'}
                </Label>
                <Input
                  type="url"
                  value={formData.platform_link}
                  onChange={(e) => setFormData({...formData, platform_link: e.target.value})}
                  placeholder={`https://${platform === 'apple_music' ? 'music.apple.com' : platform}.com/...`}
                  required
                  className="bg-zinc-900 border-zinc-800 text-white mt-2"
                />
              </div>

              {/* ÉTAPE 2 : Objectif de promotion */}
              <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
                <h3 className="text-white font-bold mb-3">🎯 ÉTAPE 2 : Que voulez-vous promouvoir ?</h3>
                <Label className="text-white">Choisissez votre objectif *</Label>
                <Select 
                  value={formData.promotion_goal} 
                  onValueChange={(value) => setFormData({...formData, promotion_goal: value})} 
                  required
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white mt-2">
                    <SelectValue placeholder="Sélectionnez votre objectif" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Label className="text-white mt-4 block">Quantité souhaitée *</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  placeholder="Ex: 1000, 5000, 10000..."
                  required
                  min="1"
                  className="bg-zinc-900 border-zinc-800 text-white mt-2"
                />
                <p className="text-xs text-zinc-400 mt-1">
                  Nombre de {formData.promotion_goal || 'résultats'} souhaités
                </p>
              </div>

              {/* Informations de paiement - Caché si Admin Adil */}
              {!isAdminAdil && (
                <div className="bg-purple-900/20 border border-purple-800 rounded-lg p-4">
                  <h3 className="text-white font-bold mb-3">💳 Informations de paiement</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white">Opérateur Mobile Money *</Label>
                      <Select value={operator} onValueChange={setOperator} required>
                        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white mt-2">
                          <SelectValue placeholder="Choisissez votre opérateur" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ORANGE_MONEY">Orange Money</SelectItem>
                          <SelectItem value="MPESA">M-Pesa</SelectItem>
                          <SelectItem value="AIRTEL_MONEY">Airtel Money</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-white">Numéro de téléphone (Mobile Money) *</Label>
                      <Input
                        type="tel"
                        value={formData.phone_number}
                        onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                        placeholder="+243..."
                        required
                        className="bg-zinc-900 border-zinc-800 text-white mt-2"
                      />
                      <p className="text-xs text-zinc-500 mt-1">
                        Vous recevrez une notification pour confirmer le paiement
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || paymentInitiated}
                className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600' : iconColor.replace('bg-', 'bg-')} text-white py-6 text-lg font-bold`}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={20} />
                    {isAdminAdil ? 'Enregistrement...' : 'Initiation du paiement...'}
                  </>
                ) : paymentInitiated ? (
                  isAdminAdil ? 'Commande enregistrée !' : 'Paiement initié - Confirmez sur votre téléphone'
                ) : (
                  isAdminAdil ? 'GRATUIT (Admin Adil) - Commander' : 'Payer - 35$'
                )}
              </Button>

              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-200">
                  <strong>Garanties :</strong>
                </p>
                <ul className="text-sm text-blue-200 mt-2 space-y-1">
                  <li>✓ Résultats garantis sous 7-14 jours</li>
                  <li>✓ Croissance 100% organique et sécurisée</li>
                  <li>✓ Rapport détaillé après livraison</li>
                  <li>✓ Support client 24/7 : +243 978 475 357</li>
                </ul>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-200 font-bold mb-2">
                  💰 Paiement manuel (Orange Money / M-Pesa) :
                </p>
                <div className="text-sm text-yellow-200 space-y-1">
                  <p>Si le paiement automatique ne fonctionne pas, envoyez 35$ à :</p>
                  <ul className="mt-2 space-y-1 font-mono">
                    <li>📱 +243 978 475 357</li>
                    <li>📱 +243 847 722 136</li>
                  </ul>
                  <p className="mt-2 text-xs">Puis contactez-nous sur WhatsApp avec la preuve de paiement.</p>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PromotionServicePage;
